import { useCallback, useEffect, useState } from 'preact/hooks'
import { inboundWebhooksApi } from '@/api/inboundWebhooks'
import type { InboundWebhookSubscriptionViewDto, InboundWebhookSummaryDto } from '@/types/inboundWebhooks'

/**
 * A company's inbound webhook keys. Owns the list and reloading it; mutations live in the
 * components that trigger them so each surfaces its own errors.
 */
export function useInboundWebhookSubscriptions(clientCompanyId: string) {
    const [subscriptions, setSubscriptions] = useState<InboundWebhookSubscriptionViewDto[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        setLoading(true)
        const response = await inboundWebhooksApi.getSubscriptions(clientCompanyId)
        if (response.error) {
            // Deliberately not clearing the list: a failed refresh should not blank a screen
            // someone is reading. The banner says it is stale.
            setError(response.error)
        } else {
            setSubscriptions(response.data ?? [])
            setError(null)
        }
        setLoading(false)
    }, [clientCompanyId])

    useEffect(() => { void load() }, [load])

    return { subscriptions, loading, error, reload: load }
}

/**
 * The keys that can post into one workspace. Used by the workspace and web settings snippets to
 * say whether a usable key exists before someone copies a command that would 401.
 */
export function useWorkspaceInboundWebhooks(clientCompanyId: string | null, workspaceId: string | null) {
    const [webhooks, setWebhooks] = useState<InboundWebhookSummaryDto[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        // Unlike the company-scoped list above, results here belong to ONE workspace — so both
        // paths clear rather than keeping what is now another workspace's answer.
        if (!clientCompanyId || !workspaceId) {
            setWebhooks(null)
            setError(null)
            setLoading(false)
            return
        }
        setLoading(true)
        const response = await inboundWebhooksApi.getForWorkspace(clientCompanyId, workspaceId)
        if (response.error) {
            setError(response.error)
            setWebhooks(null)
        } else {
            setWebhooks(response.data ?? [])
            setError(null)
        }
        setLoading(false)
    }, [clientCompanyId, workspaceId])

    useEffect(() => { void load() }, [load])

    return { webhooks, loading, error, reload: load }
}
